import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/error-handler";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Edit,
  ShieldBan,
  ShieldCheck,
  Star,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Save,
  X,
} from "lucide-react";

const PROFILE_TYPES = [
  { value: "banda", label: "Banda" },
  { value: "artista", label: "Artista" },
  { value: "promotora", label: "Promotora" },
  { value: "manager", label: "Manager" },
  { value: "tecnico", label: "Técnico/a" },
  { value: "otro", label: "Otro" },
];

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const { user: adminUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [blockDialog, setBlockDialog] = useState<"block" | "unblock" | null>(null);
  const [blockReason, setBlockReason] = useState("");

  const { data: profile, isLoading } = useQuery({
    queryKey: ["admin-user-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: reviews } = useQuery({
    queryKey: ["admin-user-reviews", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, venues(name, slug)")
        .eq("user_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: statusLogs } = useQuery({
    queryKey: ["admin-user-status-logs", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("account_status_log" as any)
        .select("*")
        .eq("user_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!id,
  });

  const counts = {
    total: reviews?.length ?? 0,
    approved: reviews?.filter((r) => r.status === "approved").length ?? 0,
    pending: reviews?.filter((r) => r.status === "pending").length ?? 0,
    rejected: reviews?.filter((r) => r.status === "rejected").length ?? 0,
  };

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const { error } = await supabase
        .from("profiles")
        .update(data as any)
        .eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "Perfil actualizado" });
      setEditing(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  // Block/Unblock mutation
  const statusMutation = useMutation({
    mutationFn: async ({ action, reason }: { action: "block" | "unblock"; reason: string }) => {
      const newStatus = action === "block" ? "blocked" : "active";
      const oldStatus = (profile as any)?.account_status ?? "active";

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ account_status: newStatus } as any)
        .eq("id", id!);
      if (updateError) throw updateError;

      const { error: logError } = await supabase.from("account_status_log" as any).insert({
        user_id: id!,
        old_status: oldStatus,
        new_status: newStatus,
        reason: reason || null,
        changed_by: adminUser!.id,
      });
      if (logError) throw logError;
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-status-logs", id] });
      toast({ title: action === "block" ? "Usuario bloqueado" : "Usuario desbloqueado" });
      setBlockDialog(null);
      setBlockReason("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const startEditing = () => {
    if (!profile) return;
    setEditData({
      visible_name: profile.visible_name ?? "",
      profile_type: profile.profile_type ?? "",
      city: profile.city ?? "",
      bio: profile.bio ?? "",
      professional_link: profile.professional_link ?? "",
    });
    setEditing(true);
  };

  const saveEdit = () => {
    const cleaned: Record<string, any> = {};
    for (const [key, val] of Object.entries(editData)) {
      cleaned[key] = typeof val === "string" && val.trim() === "" ? null : val;
    }
    updateMutation.mutate(cleaned);
  };

  if (isLoading) {
    return (
      <AdminLayout requiredModule="usuarios_comunidad" requiredPermission="can_view">
        <div className="container py-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40" />
          <Skeleton className="h-60" />
        </div>
      </AdminLayout>
    );
  }

  if (!profile) {
    return (
      <AdminLayout requiredModule="usuarios_comunidad" requiredPermission="can_view">
        <div className="container py-16 text-center text-muted-foreground">
          Usuario no encontrado
        </div>
      </AdminLayout>
    );
  }

  const accountStatus = (profile as any).account_status ?? "active";
  const isBlocked = accountStatus === "blocked";
  const profileTypeLabel = PROFILE_TYPES.find((pt) => pt.value === profile.profile_type)?.label ?? profile.profile_type ?? "—";

  const statusColors: Record<string, string> = {
    pending: "bg-warning/10 text-warning",
    approved: "bg-success/10 text-success",
    rejected: "bg-destructive/10 text-destructive",
  };
  const statusLabels: Record<string, string> = {
    pending: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
  };

  return (
    <AdminLayout requiredModule="usuarios_comunidad" requiredPermission="can_view">
      <div className="container py-8 space-y-6">
        {/* Back link */}
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Volver a usuarios
        </Link>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-xl font-bold text-muted-foreground">
              {(profile.visible_name ?? profile.email ?? "U").charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold">
                {profile.visible_name || profile.username || "Sin nombre"}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline">{profileTypeLabel}</Badge>
                <Badge className={isBlocked ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}>
                  {isBlocked ? "Bloqueada" : "Activa"}
                </Badge>
                <span>{profile.email}</span>
                {profile.city && <span>· {profile.city}</span>}
                <Badge variant="outline" className={(profile as any).newsletter_opt_in ? "text-success" : "text-muted-foreground"}>
                  Newsletter: {(profile as any).newsletter_opt_in ? "Sí" : "No"}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Registro: {new Date(profile.created_at).toLocaleDateString("es-ES")}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Edit className="mr-1 h-4 w-4" /> Editar
            </Button>
            <Button
              variant={isBlocked ? "default" : "destructive"}
              size="sm"
              onClick={() => setBlockDialog(isBlocked ? "unblock" : "block")}
            >
              {isBlocked ? (
                <><ShieldCheck className="mr-1 h-4 w-4" /> Desbloquear</>
              ) : (
                <><ShieldBan className="mr-1 h-4 w-4" /> Bloquear</>
              )}
            </Button>
          </div>
        </div>

        {/* Edit form */}
        {editing && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Editar perfil público</CardTitle>
              <p className="text-xs text-muted-foreground">
                Estos datos se muestran públicamente en las reviews del usuario.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nombre visible</Label>
                  <Input
                    value={editData.visible_name}
                    onChange={(e) => setEditData({ ...editData, visible_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de perfil</Label>
                  <Select
                    value={editData.profile_type}
                    onValueChange={(v) => setEditData({ ...editData, profile_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROFILE_TYPES.map((pt) => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ciudad</Label>
                  <Input
                    value={editData.city}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Enlace profesional</Label>
                  <Input
                    value={editData.professional_link}
                    onChange={(e) => setEditData({ ...editData, professional_link: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                  <X className="mr-1 h-4 w-4" /> Cancelar
                </Button>
                <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
                  <Save className="mr-1 h-4 w-4" /> Guardar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity summary */}
        <div className="grid gap-4 sm:grid-cols-4">
          {[
            { label: "Enviadas", value: counts.total, icon: FileText, color: "text-foreground" },
            { label: "Aprobadas", value: counts.approved, icon: CheckCircle, color: "text-success" },
            { label: "Pendientes", value: counts.pending, icon: Clock, color: "text-warning" },
            { label: "Rechazadas", value: counts.rejected, icon: XCircle, color: "text-destructive" },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="flex items-center gap-3 p-4">
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reviews list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5" /> Reviews del usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!reviews?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Este usuario no ha enviado reviews.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sala</TableHead>
                      <TableHead>Fecha evento</TableHead>
                      <TableHead>Valoración</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Enviada</TableHead>
                      <TableHead>Editada</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((r) => {
                      const venue = r.venues as any;
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">
                            {venue?.name ?? "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.event_date ? new Date(r.event_date).toLocaleDateString("es-ES") : "—"}
                          </TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1 text-sm">
                              <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                              {r.overall_rating ? Number(r.overall_rating).toFixed(1) : "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[r.status ?? "pending"]}>
                              {statusLabels[r.status ?? "pending"]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(r.created_at).toLocaleDateString("es-ES")}
                          </TableCell>
                          <TableCell>
                            {r.is_edited && (
                              <Badge variant="outline" className="text-xs">Editada</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button asChild variant="ghost" size="sm">
                              <Link to="/admin/reviews">Ver</Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status log */}
        {statusLogs && statusLogs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historial de estado de cuenta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {statusLogs.map((log: any) => (
                  <div key={log.id} className="flex items-center gap-3 text-sm border-b pb-2 last:border-0">
                    <Badge variant="outline" className="text-xs">
                      {log.old_status} → {log.new_status}
                    </Badge>
                    <span className="text-muted-foreground">
                      {new Date(log.created_at).toLocaleString("es-ES")}
                    </span>
                    {log.reason && <span className="text-muted-foreground">— {log.reason}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Block/Unblock Dialog */}
        <Dialog open={!!blockDialog} onOpenChange={(o) => { if (!o) { setBlockDialog(null); setBlockReason(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {blockDialog === "block" ? "Bloquear usuario" : "Desbloquear usuario"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {blockDialog === "block" ? (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    El usuario no podrá iniciar sesión ni crear/editar reviews.
                  </p>
                  <Textarea
                    placeholder="Motivo del bloqueo (opcional)"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  ¿Estás seguro de que quieres reactivar esta cuenta?
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setBlockDialog(null); setBlockReason(""); }}>
                  Cancelar
                </Button>
                <Button
                  variant={blockDialog === "block" ? "destructive" : "default"}
                  onClick={() => {
                    if (blockDialog) {
                      statusMutation.mutate({ action: blockDialog, reason: blockReason });
                    }
                  }}
                  disabled={statusMutation.isPending}
                >
                  {blockDialog === "block" ? "Bloquear" : "Desbloquear"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
